import { Component } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '../../theme/ThemeComponent';
import { intersection } from 'lodash';

/**
 * Sticky filters component for collection pages
 * Adds 'sticky' class when the collection-main__filter-sorting-wrap reaches the top of the page
 * Removes sticky when scrolled past the .collection-main__wrapper section
 * Features smooth transitions and optimized performance
 *
 * Usage: <sticky-filter class="collection-main__filter-sorting-wrap">
 */
@Component('sticky-filter')
export class StickyFilters extends ThemeComponent {

    private wrapperEl: HTMLElement | null = null;
    private containerEl: HTMLElement | null = null;
    private rafId: number | null = null;
    private isSticky = false;
    private elementHeight = 0;
    private resizeObserver: ResizeObserver | null = null;
    private spacerEl: HTMLDivElement | null = null;
    private boundaryThreshold = 8; // px hysteresis to avoid flicker near boundaries

    init() {
        // Always enable sticky styling wrapper; logic will control class on the element
        document.body.classList.add('collection-filter-sticky');

        // Cache wrapper and measure
        this.wrapperEl = document.querySelector('.collection-main__wrapper');
        this.containerEl = document.querySelector('.collection-main__container');
        // Create spacer to avoid layout shift when element becomes fixed
        this.ensureSpacer();
        this.measure();

        // Listen to scroll/resize with rAF throttle
        this.$listen(window, 'scroll', () => this.requestTick());
        this.$listen(window, 'resize', () => {
            this.measure();
            this.requestTick();
        });

        // Observe layout changes inside wrapper (handles load more/less)
        if (this.wrapperEl && 'ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(() => {
                this.measure();
                this.updateStickyState();
            });
            this.resizeObserver.observe(this.wrapperEl);
        }

        // Initial state
        this.updateStickyState();
    }

    private requestTick() {
        if (this.rafId !== null) {return;}
        this.rafId = window.requestAnimationFrame(() => {
            this.rafId = null;
            this.updateStickyState();
        });
    }

    private measure() {
        this.elementHeight = this.getBoundingClientRect().height;
        if (this.spacerEl) {
            this.spacerEl.style.height = this.isSticky ? `${this.elementHeight}px` : '0px';
        }
    }

    private ensureSpacer() {
        if (!this.spacerEl) {
            this.spacerEl = document.createElement('div');
            this.spacerEl.setAttribute('aria-hidden', 'true');
            this.spacerEl.style.width = '100%';
            this.spacerEl.style.height = '0px';
            this.spacerEl.style.pointerEvents = 'none';
            if (this.parentNode) {
                this.parentNode.insertBefore(this.spacerEl, this.nextSibling);
            }
        }
    }

    private updateStickyState() {
        const selfRect = this.getBoundingClientRect();
        const wrapperRect = this.wrapperEl?.getBoundingClientRect() || null;
        const containerRect = this.containerEl?.getBoundingClientRect() || null;

        // Determine if header should stick: only within collection-main container bounds
        const containerReachedTop = containerRect ? containerRect.top <= this.boundaryThreshold : false;
        // Add hysteresis near the bottom so we don't flap when crossing boundary
        const wrapperBottomWithThreshold = wrapperRect
            ? (wrapperRect.bottom - this.boundaryThreshold)
            : null;
        const withinWrapper = wrapperBottomWithThreshold === null
            ? true
            : wrapperBottomWithThreshold > Math.max(0, this.elementHeight);

        const stickyBar = containerReachedTop && withinWrapper;
        if (stickyBar !== this.isSticky) {
            this.isSticky = stickyBar;
            this.toggleStickyClass(stickyBar);
            // Update spacer immediately to keep layout stable
            if (this.spacerEl) {
                this.spacerEl.style.height = stickyBar ? `${this.elementHeight}px` : '0px';
            }
        }
    }

    private toggleStickyClass(stickyBar: boolean) {
        if (stickyBar) {
            this.classList.add('sticky');
        } else {
            this.classList.remove('sticky');
        }
    }

    disconnectedCallback() {
        // Cleanup observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        super.disconnectedCallback();
    }
}
